import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const EMPTY = {
  tower: "A", tower_name: "", floor: 1, flat_number: "", bhk_type: "2BHK",
  sqft_area: "", owner_name: "", owner_phone: "", owner_email: "", owner_address: "",
  parking_slot: "", maintenance_amount: "", monthly_rent: "", furnishing_status: "Unfurnished",
  possession_letter_no: "", handover_date: "",
  occupancy_type: "vacant",
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-medium text-slate-600">{label}</Label>
    {children}
  </div>
);

export default function AddFlatDialog({ open, onOpenChange, onSaved, editFlat }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!editFlat;

  useEffect(() => {
    if (editFlat) {
      setForm({ ...EMPTY, ...editFlat,
        sqft_area: editFlat.sqft_area ?? "", maintenance_amount: editFlat.maintenance_amount ?? "",
        monthly_rent: editFlat.monthly_rent ?? "" });
    } else {
      setForm(EMPTY);
    }
  }, [editFlat, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.flat_number || !form.owner_name) {
      toast.error("Flat number and owner name are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      floor: parseInt(form.floor) || 0,
      sqft_area: form.sqft_area ? parseFloat(form.sqft_area) : null,
      maintenance_amount: form.maintenance_amount ? parseFloat(form.maintenance_amount) : null,
      monthly_rent: form.monthly_rent ? parseFloat(form.monthly_rent) : null,
      possession_letter_no: form.possession_letter_no || null,
      handover_date: form.handover_date || null,
      tower_name: form.tower_name || form.tower,
    };
    try {
      if (isEdit) {
        await api.put(`/flats/${editFlat.id}`, payload);
        toast.success("Flat updated");
      } else {
        await api.post("/flats", payload);
        toast.success("Flat added");
      }
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-flat-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isEdit ? "Edit Flat" : "Add New Flat"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 py-2">
          <Field label="Tower">
            <Input value={form.tower} onChange={(e) => set("tower", e.target.value.toUpperCase())} data-testid="flat-tower-input" />
          </Field>
          <Field label="Tower Name">
            <Input value={form.tower_name} onChange={(e) => set("tower_name", e.target.value)} placeholder="Apex" data-testid="flat-tower-name-input" />
          </Field>
          <Field label="Floor">
            <Input type="number" value={form.floor} onChange={(e) => set("floor", e.target.value)} data-testid="flat-floor-input" />
          </Field>
          <Field label="Flat Number">
            <Input value={form.flat_number} onChange={(e) => set("flat_number", e.target.value)} placeholder="101" data-testid="flat-number-input" />
          </Field>
          <Field label="BHK Type">
            <Select value={form.bhk_type} onValueChange={(v) => set("bhk_type", v)}>
              <SelectTrigger data-testid="flat-bhk-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["1BHK", "2BHK", "3BHK", "4BHK", "5BHK"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Area (sq.ft)">
            <Input type="number" value={form.sqft_area} onChange={(e) => set("sqft_area", e.target.value)} data-testid="flat-area-input" />
          </Field>
          <Field label="Owner Name">
            <Input value={form.owner_name} onChange={(e) => set("owner_name", e.target.value)} data-testid="flat-owner-name-input" />
          </Field>
          <Field label="Owner Phone">
            <Input value={form.owner_phone} onChange={(e) => set("owner_phone", e.target.value)} data-testid="flat-owner-phone-input" />
          </Field>
          <Field label="Owner Email">
            <Input value={form.owner_email} onChange={(e) => set("owner_email", e.target.value)} data-testid="flat-owner-email-input" />
          </Field>
          <Field label="Parking Slot">
            <Input value={form.parking_slot} onChange={(e) => set("parking_slot", e.target.value)} placeholder="P-14B" data-testid="flat-parking-input" />
          </Field>
          <Field label="Maintenance (₹/mo)">
            <Input type="number" value={form.maintenance_amount} onChange={(e) => set("maintenance_amount", e.target.value)} data-testid="flat-maintenance-input" />
          </Field>
          <Field label="Expected Rent (₹/mo)">
            <Input type="number" value={form.monthly_rent} onChange={(e) => set("monthly_rent", e.target.value)} data-testid="flat-rent-input" />
          </Field>
          <Field label="Furnishing">
            <Select value={form.furnishing_status} onValueChange={(v) => set("furnishing_status", v)}>
              <SelectTrigger data-testid="flat-furnishing-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Unfurnished", "Semi-Furnished", "Fully Furnished"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Base Occupancy">
            <Select value={form.occupancy_type} onValueChange={(v) => set("occupancy_type", v)}>
              <SelectTrigger data-testid="flat-occupancy-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vacant">Vacant</SelectItem>
                <SelectItem value="owner_occupied">Owner Occupied</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 space-y-3">
          <p className="text-[11px] uppercase font-mono tracking-[0.16em] text-amber-700">Flat Handover / Possession</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Possession Letter No.">
              <Input value={form.possession_letter_no} onChange={(e) => set("possession_letter_no", e.target.value)} placeholder="PL-2021-0456" data-testid="flat-possession-letter-input" />
            </Field>
            <Field label="Handover Date">
              <Input type="date" value={form.handover_date} onChange={(e) => set("handover_date", e.target.value)} data-testid="flat-handover-date-input" />
            </Field>
          </div>
          <p className="text-xs text-amber-700">A tenant cannot be added until both handover details are recorded.</p>
        </div>
        <p className="text-xs text-slate-400">Status becomes "Rented" automatically when an active tenant is added.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="flat-cancel-button">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0F2942] gap-2" data-testid="flat-save-button">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? "Save Changes" : "Add Flat"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
